import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { setAuthData, clearAuthData, setGoogleInfo, getAuthToken } from '../utils/tokenManager';
import type { 
  RegisterBySmsRequest, 
  RegisterByEmailRequest, 
  AuthResponse,
  LoginRequest,
  SendOtpBySmsRequest,
  SendOtpByEmailRequest,
  VerifyOtpRequest,
  OtpResponse
} from '../types/auth';

/**
 * Register a new user via SMS (phone number)
 */
export const registerBySms = async (data: RegisterBySmsRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.REGISTER_SMS}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Registration (SMS) error response:', errorData);
    
    let errorMessage = `Registration failed with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Register a new user via Email
 */
export const registerByEmail = async (data: RegisterByEmailRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.REGISTER_EMAIL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Registration (Email) error response:', errorData);
    
    let errorMessage = `Registration failed with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Register a new user (automatically selects SMS or Email based on provided data)
 * Priority: Phone number > Email
 */
export const register = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber?: string
): Promise<AuthResponse> => {
  // Prioritize phone number registration if available
  if (phoneNumber && phoneNumber.trim() !== '') {
    return registerBySms({
      phone: phoneNumber,
      first_name: firstName,
      last_name: lastName,
    });
  }

  // Fallback to email registration
  return registerByEmail({
    email,
    first_name: firstName,
    last_name: lastName,
  });
};

/**
 * Login user
 * Automatically caches token and user data on success
 */
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGIN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Login error response:', errorData);
    
    // Extract meaningful error message from backend response
    let errorMessage = `Login failed with status ${response.status}`;
    
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    console.log('❌ Error message to display:', errorMessage);
    throw new Error(errorMessage);
  }

  const result: AuthResponse = await response.json();
  
  console.log('Login API response:', result);

  // Cache token and user data if login successful
  if (result.success && result.data?.token && result.data?.user) {
    setAuthData(result.data.token, result.data.user);
    console.log('✅ Token and user data cached successfully');
  } else {
    console.error('❌ No token in response. Response structure:', Object.keys(result));
  }

  return result;
};

/**
 * Send OTP via SMS
 * Uses /auth/register/sms endpoint
 */
export const sendOtpBySms = async (data: SendOtpBySmsRequest): Promise<OtpResponse> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.REGISTER_SMS}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Send OTP (SMS) error response:', errorData);
    
    let errorMessage = `Failed to send OTP with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Send OTP via Email
 * Uses /auth/register/email endpoint
 */
export const sendOtpByEmail = async (data: SendOtpByEmailRequest): Promise<OtpResponse> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.REGISTER_EMAIL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Send OTP (Email) error response:', errorData);
    
    let errorMessage = `Failed to send OTP with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Send OTP (automatically selects SMS or Email based on method)
 */
export const sendOtp = async (
  contact: string,
  method: 'email' | 'sms' | 'phone'
): Promise<OtpResponse> => {
  if (method === 'sms' || method === 'phone') {
    return sendOtpBySms({ phone: contact });
  } else {
    return sendOtpByEmail({ email: contact });
  }
};

/**
 * Verify OTP code and complete registration
 * Automatically caches token and user data on success
 */
export const verifyOtp = async (
  contact: string,
  otpCode: string,
  method: 'email' | 'sms' | 'phone'
): Promise<AuthResponse> => {
  const requestBody: VerifyOtpRequest = {
    otp_code: otpCode,
    first_name: 'NA',
    last_name: 'NA',
    ...(method === 'email' ? { email: contact } : { phone: contact })
  };

  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.VERIFY_OTP}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ OTP verification error response:', errorData);
    
    // Extract meaningful error message from backend response
    let errorMessage = `OTP verification failed with status ${response.status}`;
    
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    console.log('❌ Error message to display:', errorMessage);
    throw new Error(errorMessage);
  }

  const result: AuthResponse = await response.json();

  // Automatically cache token and user data if verification successful
  if (result.success && result.data?.token && result.data?.user) {
    setAuthData(result.data.token, result.data.user);
    console.log('Authentication data cached successfully');
  }

  return result;
};

/**
 * Change user password
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found. Please sign in again.');
  }

  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    console.log('❌ Change password error response:', data);
    
    let errorMessage = `Failed to change password with status ${response.status}`;
    if (data.error?.message) {
      errorMessage = data.error.message;
      if (data.error.suggestion) {
        errorMessage += `. ${data.error.suggestion}`;
      }
    } else if (data.message) {
      errorMessage = data.message;
    }
    
    throw new Error(errorMessage);
  }

  return { success: true, message: data.message || 'Password changed successfully' };
};

/**
 * Google OAuth Login/Registration
 * Sends Google credential token to backend for verification
 * @param credentialToken - Google access token
 * @param intent - Optional intent: 'login' or 'signup'
 */
export const googleLogin = async (
  credentialToken: string, 
  intent?: 'login' | 'signup'
): Promise<AuthResponse> => {
  const requestBody: { token: string; intent?: string } = {
    token: credentialToken
  };
  
  // Only include intent if provided
  if (intent) {
    requestBody.intent = intent;
  }

  console.log('🔵 Sending Google auth request with intent:', intent);
  console.log('🔵 Request body:', requestBody);

  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.GOOGLE_AUTH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Google auth error response:', errorData);
    
    // Extract meaningful error message from backend response
    let errorMessage = `Google authentication failed with status ${response.status}`;
    
    if (errorData.error?.message) {
      // Backend returns structured error: { error: { message: "...", suggestion: "..." } }
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      // Backend returns simple error: { message: "..." }
      errorMessage = errorData.message;
    }
    
    console.log('❌ Error message to display:', errorMessage);
    throw new Error(errorMessage);
  }

  const result: AuthResponse = await response.json();

  console.log('Google auth API response:', result);

  if (result.success && result.data) {
    console.log('Auth type:', result.data.auth_type);
    console.log('Auth provider:', result.data.auth_provider);
    console.log('First time user:', result.data.is_first_time_user);
    console.log('Requires profile creation:', result.data.requires_profile_creation);

    // Handle registration_pending - store google_info for profile creation (legacy)
    if (result.data.auth_type === 'registration_pending' && result.data.google_info) {
      console.log('📧 Google info from backend:', result.data.google_info);
      console.log('📧 Storing for profile creation');
      setGoogleInfo(result.data.google_info);
      console.log('✅ Google info stored, profile creation required');
    } 
    // Handle successful login/registration with token
    else if (result.data.token && result.data.user) {
      console.log('📧 User data from backend:', result.data.user);
      console.log('📧 User email:', result.data.user.email);
      console.log('📧 User auth_provider:', result.data.user.auth_provider);
      setAuthData(result.data.token, result.data.user);
      console.log('✅ Google auth successful, token cached');
    }
    else {
      console.error('❌ Unexpected response format - no token or google_info');
    }
  } else {
    console.error('❌ Google auth failed');
  }

  return result;
};

/**
 * Logout user
 * Clears all authentication data from localStorage
 */
export const logout = async (): Promise<void> => {
  try {
    // Optional: Call backend logout endpoint if it exists
    // This is useful for invalidating tokens on the server side
    // const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGOUT}`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${getAuthToken()}`
    //   },
    // });
    
    // Clear authentication data from localStorage
    clearAuthData();
    console.log('✅ User logged out successfully');
  } catch (error) {
    console.error('Logout error:', error);
    // Clear local data even if API call fails
    clearAuthData();
  }
};

/**
 * Send forgot password OTP
 */
export const sendForgotPasswordOtp = async (data: { email?: string; phone?: string }): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Forgot password error response:', errorData);
    
    let errorMessage = `Failed to send OTP with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  const result = await response.json();
  console.log('✅ Forgot password OTP sent successfully');
  return result;
};

/**
 * Reset password with OTP
 */
export const resetPassword = async (data: { 
  email?: string; 
  phone?: string; 
  otp: string; 
  new_password: string 
}): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('❌ Reset password error response:', errorData);
    
    let errorMessage = `Failed to reset password with status ${response.status}`;
    if (errorData.error?.message) {
      errorMessage = errorData.error.message;
      if (errorData.error.suggestion) {
        errorMessage += `. ${errorData.error.suggestion}`;
      }
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
    
    throw new Error(errorMessage);
  }

  const result = await response.json();
  console.log('✅ Password reset successfully');
  return result;
};
